import { useEffect, useMemo, useState } from "react";
import { bool, func, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Rounding, RoundIntervals, BreakdownTableDepths } from "@repo/utils";
import { useEstimateStructure, RoundingSettingsContents } from "@tools";
import { Dialog, Button, Tag, Accordion, Checkbox } from "@components";
import { Grid, Switch } from "@material-ui/core";
import { Percent } from "@assets";

export const BreakdownRoundings = observer(({
  open,
  workType,
  onClose,
}) => {
  
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  
  const [ roundingsUsed, setRoundingsUsed ] = useState(false);
  const [ localType, setType ] = useState(1);
  const [ localInterval, setInterval ] = useState(RoundIntervals.FULL);
  const [ localLevel, setLevel ] = useState(BreakdownTableDepths.TASK);
  const [ toAllSections, setToAllSections ] = useState(false);
  
  const { name, color, roundType, roundInterval, roundLevel, hasRounding } = workType;
  
  const disabled = useMemo(() => (
    roundingsUsed && (typeof localType !== "number" || typeof localInterval !== "number")
  ), [ roundingsUsed, localType, localInterval ]);
  
  useEffect(() => {
    setType(hasRounding ? roundType : Rounding.FULL);
    setInterval(hasRounding ? roundInterval : RoundIntervals.FULL);
    setLevel(hasRounding ? roundLevel : BreakdownTableDepths.TASK);
    setRoundingsUsed(true);
  }, [ roundType, roundInterval, roundLevel, hasRounding ]);
  
  const handleExpand = () => {
    setRoundingsUsed((state) => !state);
  };
  
  const saveRoundingSettings = () => {
    onClose();
    if (roundingsUsed) {
      if(toAllSections) {
        structure.setWorkTypeRounding(workType.id, localType, localInterval, localLevel);
      } else {
        workType.setRounding(localType, localInterval, localLevel, true);
      }
    } else
      workType.setRounding(undefined, undefined, undefined, true);
  };

  const actions = (
    <>
      <Button
        variant="outlined"
        className="w-max"
        onClick={onClose}
      >
        {t("common.cancel")}
      </Button>
      <Button
        variant="contained"
        className="w-max"
        onClick={saveRoundingSettings}
        disabled={disabled}
      >
        {t("common.apply")}
      </Button>
    </>
  );

  return (
    <Dialog
      open={open}
      title={
        <Grid container alignItems="center">
          <h2>{t("views.editor.dialogs.roundings.title")}</h2>
          <Tag color={color} text={name} className="ml-2 mt-1-5" />
        </Grid>
      }
      onClose={onClose}
      actions={actions}
      width={600}
    >
      <Grid item container alignItems="flex-start">
        <Accordion
          hideExpandButton
          expanded={roundingsUsed}
          title={
          
            <Grid item container justifyContent="space-between" alignItems="center">
              <Grid item container wrap="nowrap" alignItems="center" className="w-max">
                <Percent className="color-primary text-2xl mr-2" />
                {t("views.editor.dialogs.roundings.subtitle")}
                {": "}
              </Grid>
              <Switch
                color="primary"
                checked={roundingsUsed}
                onChange={handleExpand}
                name={
                  roundingsUsed
                    ? "Disable roundings"
                    : "Enable roundings"
                }
              />
            </Grid>
          }
          variant="info"
          bordered={false}
          titleColored
        >
          <RoundingSettingsContents
            type={localType}
            level={localLevel}
            interval={localInterval}
            onTypeChange={setType}
            onLevelChange={setLevel}
            onIntervalChange={setInterval}
          />
          <Grid item container className="mt-3 gap-2">
            <Checkbox
              name="applyToAllSections"
              checked={toAllSections}
              label={t("common.all_sections_apply")}
              onChange={() => setToAllSections(!toAllSections)}
            />
          </Grid>
        </Accordion>
      </Grid>
    </Dialog>
  );
});

BreakdownRoundings.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  workType: object.isRequired,
};
